import app from '../../../authentication/firebaseStart';
import { getKioskDevices } from '../../../data/FirebaseUtil';
import { DEVICE_STATUS } from '../constant/Dashboard.constant';

const firestore = app.firestore();


const formatListData = (doc) => {
  return {
    __name__: doc.id || doc.__name__ || '',
    region: doc.region || '',
    serialNumber: doc.serialNumber || '',
    deviceState: doc.deviceState || '',
    checkOutState: doc.checkOutState || '',
    userId: doc.userId || '',
    batteryLevel: doc.batteryLevel || '',
    siteName: doc.siteName || '',
    kioskName: doc.kioskName || '',
    assetType: doc.assetType || '',
    reportedAt: doc.reportedAt || '',
    status: doc.status || '',
    modelNumber: doc.modelNumber || '',
    statusReason: doc.statusReason || ''
  }
}
// Rest of the helper functions remain the same
const executeQuery = async (query) => {
  const snapshot = await query.get();
  const data = {
    devices: snapshot.docs.map(doc => formatListData(doc)),
    cursors: {
      first: snapshot.docs[0] || null,
      last: snapshot.docs[snapshot.docs.length - 1] || null
    }
  };
  return data;
};

// Apply pagination to query
const applyPagination = (query, { startAfterDoc, endBeforeDoc, startAtDoc, limit }) => {
  let paginatedQuery = query;

  if (startAtDoc) {
    paginatedQuery = paginatedQuery.startAt(startAtDoc);
  } else if (startAfterDoc) {
    paginatedQuery = paginatedQuery.startAfter(startAfterDoc);
  } else if (endBeforeDoc) {
    paginatedQuery = paginatedQuery.endBefore(endBeforeDoc);
  }

  if (limit) {
    paginatedQuery = endBeforeDoc ? 
      paginatedQuery.limitToLast(limit) : 
      paginatedQuery.limit(limit);
  }

  return paginatedQuery;
};

// Create query with search and filters
const createSortQuery = ({ tenantName, orderBy, orderDirection, kiosks, state }) => {
    const { available, inUse, missing, beingFound } = DEVICE_STATUS;
    let baseQuery = firestore.collection('tenants').doc(tenantName).collection('Devices').where('kioskName', 'in', kiosks);
    const query = state === 'available' 
    ? baseQuery.where('deviceState', '==', available).where('checkOutState', '==', false).where('isCharging', '==', true) 
    : state === 'inUse' ? baseQuery.where('deviceState', '==', inUse).where('checkOutState', '==', true).where('isCharging', '==', false)
    : baseQuery.where('deviceState' in [missing, beingFound]).where('checkOutState', '==', false).where('isCharging', '==', false)
    return query.orderBy(orderBy, orderDirection);
};

// Main function to fetch users with pagination
export const fetchDeviceList = async ({
  limit = 10,
  startAfterDoc = null,
  endBeforeDoc = null,
  startAtDoc = null,
  orderBy = 'reportedAt',
  orderDirection = 'desc',
  companyID,
  totalCount = null,
  state = 'available',
  kiosks,
  deviceStates,
  checkOutState, 
  isCharging,
  isLastPage = false,
  isFirstPage = false,
  isDashboardPage
}) => {
  console.log(`Fetching device with params:`, {
    limit,
    orderBy,
    orderDirection,
    totalCount,
    state,
    kiosks,
    hasStartAfter: !!startAfterDoc,
    hasEndBefore: !!endBeforeDoc,
    hasStartAt: !!startAtDoc
  });

  try {
    const deviceResult = await getKioskDevices({
      orderBy,
      orderDirection,
      startAfterDoc,
      endBeforeDoc,
      startAtDoc,
      limit,
      companyID,
      kioskNamesList: kiosks,
      fromAllData: true,
      deviceStates,
      checkOutState, 
      isCharging,
      isLastPage,
      isFirstPage,
      isDashboardPage
    })
    if (!deviceResult.status) {
      return {
        devices: [],
        cursors: {
          first: null,
          last: null
        }
      };
    }
    const data = {
      devices: deviceResult?.data?.map(doc => formatListData(doc)),
      cursors: {
        first: deviceResult?.data[0]?.__name__ || null,
        last: deviceResult?.data[deviceResult.data.length - 1]?.__name__ || null
      }
    };
    return data;
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
};

export const fetchLastPage = async ({
  tenantName,
  limit,
  orderBy = 'reportedAt',
  orderDirection = 'desc',
  totalCount = null,
  kiosks,
  state
}) => {
  try {
    const totalPages = Math.floor(totalCount / limit);
    const remainingRecords = totalCount - (totalPages * limit);
    const lastPageLimit = remainingRecords > 0 ? remainingRecords : limit;

    const query = createSortQuery({ 
      tenantName, 
      orderBy,
      orderDirection,
      kiosks,
      state
    });
  const snapshot = await query.limitToLast(lastPageLimit).get()
  return {
    devices: snapshot.docs.map(doc => formatListData(doc)),
    cursors: {
      first: snapshot.docs[0] || null,
      last: snapshot.docs[snapshot.docs.length - 1] || null
    }
  };
  } catch (error) {
    console.error('Error fetching last page:', error);
    throw error;
  }
};

// Get total user count
export const getDeviceCount = async (tenantName, kiosks) => {
  try {
    const countAPIs = [];
    const { available, inUse, missing, beingFound } = DEVICE_STATUS;
    let baseQuery = firestore.collection('tenants').doc(tenantName).collection('Devices').where('kioskName', 'in', kiosks);
      // Available - where status = 1
      countAPIs.push(baseQuery.where('deviceState', '==', available).where('checkOutState', '==', false).where('isCharging', '==', true).get());
      // Missing -  where status = 2
      countAPIs.push(baseQuery.where('deviceState' in [missing, beingFound]).where('checkOutState', '==', false).where('isCharging', '==', false).get());
      // In Use -  where status = 3
      countAPIs.push(baseQuery.where('deviceState', '==', inUse).where('checkOutState', '==', true).where('isCharging', '==', false).get());
      const countDocs = await Promise.all(countAPIs)
      const count = countDocs.map((count) => count.size)
      return { available: count[0], missing: count[1], inUse: count[2]};
  } catch (error) {
    console.error('Error fetching device count:', error);
    throw error;
  }
};

export default { fetchDeviceList, fetchLastPage, getDeviceCount };